/**
 * HitlReceipt (L4 · Figma 131:87) — post-handback receipt card.
 *
 * Renders once the session has ended (handback landed). Shows:
 *   - Session length + instructor identity (blurred to just first name)
 *   - Concepts covered (the amber chips the instructor picked)
 *   - "Watch for" note
 *   - Any annotations pinned during the session
 *   - CTA to close / continue lecture
 *
 * Hydrated on mount from GET /hitl/sessions/:id/receipt.
 */

import React, { useEffect, useState } from 'react';
import { hitlApi, isHitlUnavailable, Receipt } from '../../services/hitl';

const HITL_AMBER = '#B8600B';
const HITL_AMBER_LIGHT = '#F5E6D8';

interface Props {
  sessionId: string;
  onDismiss: () => void;
}

function fmtDuration(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m} min`;
}

const HitlReceipt: React.FC<Props> = ({ sessionId, onDismiss }) => {
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await hitlApi.getReceipt(sessionId);
        if (!cancelled) setReceipt(r);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load receipt');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: 12,
          background: '#FEF2F2',
          borderRadius: 8,
          color: '#B91C1C',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {error}
      </div>
    );
  }
  if (!receipt) {
    return (
      <div
        style={{
          padding: 12,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: '#52525B',
        }}
      >
        Loading receipt…
      </div>
    );
  }

  const chips = receipt.handback?.covered_chips ?? [];
  const watchFor = receipt.handback?.watch_for_note ?? null;
  const acknowledgeAndDismiss = async () => {
    setAcknowledging(true);
    setAckError(null);
    try {
      await hitlApi.acknowledgeReceipt(sessionId);
      onDismiss();
    } catch (err) {
      setAckError(
        isHitlUnavailable(err)
          ? 'Human instructor support is currently disabled. Try again when it is available.'
          : err instanceof Error
          ? err.message
          : 'The session receipt could not be acknowledged',
      );
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div
      style={{
        padding: 20,
        background: '#fff',
        border: `1px solid ${HITL_AMBER}`,
        borderRadius: 8,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#18181B',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div
          aria-hidden
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: '#DCFCE7',
            color: '#15803D',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          ✓
        </div>
        <h3 style={{ margin: 0, fontSize: 18 }}>Session wrap-up</h3>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            color: '#71717A',
          }}
        >
          {fmtDuration(receipt.session.session_length_ms)}
        </span>
      </div>

      {chips.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: '#52525B', marginBottom: 6 }}>
            Concepts covered
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {chips.map((c) => (
              <span
                key={c.slug}
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  background: HITL_AMBER_LIGHT,
                  color: HITL_AMBER,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {c.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {watchFor && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: HITL_AMBER_LIGHT,
            borderLeft: `3px solid ${HITL_AMBER}`,
            borderRadius: 4,
            fontSize: 14,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: HITL_AMBER, marginBottom: 4 }}>
            Watch for next
          </div>
          {watchFor}
        </div>
      )}

      {receipt.annotations.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: '#52525B', marginBottom: 6 }}>
            Instructor annotations
          </div>
          {receipt.annotations.map((a) => (
            <div
              key={a.id}
              style={{
                padding: 8,
                marginBottom: 6,
                background: '#F4F4F5',
                borderRadius: 4,
                fontSize: 13,
              }}
            >
              <div style={{ color: '#71717A', fontSize: 11 }}>
                Lines {a.line_range_start}–{a.line_range_end}
                {a.file_path ? ` · ${a.file_path}` : ''}
              </div>
              {a.note}
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => void acknowledgeAndDismiss()}
        disabled={acknowledging}
        style={{
          padding: '10px 16px',
          borderRadius: 6,
          border: 'none',
          background: HITL_AMBER,
          color: '#fff',
          fontWeight: 500,
          fontFamily: 'inherit',
          fontSize: 14,
          cursor: acknowledging ? 'wait' : 'pointer',
          opacity: acknowledging ? 0.65 : 1,
        }}
      >
        {acknowledging ? 'Saving…' : ackError ? 'Retry and continue' : 'Continue'}
      </button>
      {ackError && (
        <div role="alert" style={{ marginTop: 8, color: '#B91C1C', fontSize: 12 }}>
          Your receipt is still open. {ackError}
        </div>
      )}
    </div>
  );
};

export default HitlReceipt;
